import { BrowserProvider } from "ethers";
import { http } from "./api";

const BSC_CHAIN_ID_HEX = "0x38"; // 56
const BSC_CHAIN_ID = 56;
const WC_PROJECT_ID =
  process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || "";

export const walletConfig = async () => {
  const { data } = await http.get("/wallet/config");
  return data;
};

export const walletChallenge = async (address) => {
  const { data } = await http.post("/wallet/challenge", { address });
  return data;
};

export const walletVerify = async (address, signature) => {
  const { data } = await http.post("/wallet/verify", { address, signature });
  return data;
};

export const walletRefresh = async () => {
  const { data } = await http.post("/wallet/refresh");
  return data;
};

export const hasEthereumProvider = () =>
  typeof window !== "undefined" && !!window.ethereum;

/**
 * When the user has multiple wallet extensions installed (MetaMask + Rabby +
 * Coinbase, etc.), they often race to inject `window.ethereum` and some
 * wrappers mangle RPC calls — leading to errors like "Broadcast channel
 * unavailable" when a non-MetaMask wrapper swallows an eth_requestAccounts
 * call it doesn't fully implement.
 *
 * Prefer EIP-5749 `providers[]` → MetaMask first, then whatever `window.ethereum`
 * ultimately points at as a fallback. Returns the chosen provider.
 */
export function pickInjectedProvider() {
  if (typeof window === "undefined" || !window.ethereum) return null;
  const eth = window.ethereum;
  const providers = Array.isArray(eth.providers) ? eth.providers : null;
  if (providers && providers.length) {
    const mm = providers.find((p) => p && p.isMetaMask && !p.isBraveWallet);
    if (mm) return mm;
    const anyWallet = providers.find((p) => p && (p.isMetaMask || p.isTrust || p.isCoinbaseWallet));
    if (anyWallet) return anyWallet;
    return providers[0];
  }
  return eth;
}

/**
 * MetaMask (MV3) runs in a service worker that Chrome kills on idle. The very
 * first RPC call after idle can throw transient errors ("Broadcast channel
 * unavailable", "Could not establish connection", "Receiving end does not
 * exist") while the service worker spins back up. We retry such calls once
 * after a short delay to transparently handle the wake-up.
 */
function isTransientWakeError(e) {
  const msg = ((e && (e.message || e.toString())) || "").toLowerCase();
  return (
    msg.includes("broadcast channel") ||
    msg.includes("could not establish connection") ||
    msg.includes("receiving end does not exist") ||
    msg.includes("extension context") ||
    msg.includes("message port closed")
  );
}

async function requestWithWakeRetry(provider, args, retries = 1) {
  try {
    return await provider.request(args);
  } catch (e) {
    if (retries > 0 && isTransientWakeError(e)) {
      await new Promise((r) => setTimeout(r, 600));
      return requestWithWakeRetry(provider, args, retries - 1);
    }
    throw e;
  }
}

export const isMobile = () =>
  typeof navigator !== "undefined" &&
  /android|webos|iphone|ipad|ipod|opera mini|iemobile|mobile/i.test(navigator.userAgent || "");

export const isInWalletDappBrowser = () => {
  if (typeof window === "undefined") return false;
  const eth = window.ethereum;
  return !!(
    eth &&
    (eth.isMetaMask ||
      eth.isTrust ||
      eth.isCoinbaseWallet ||
      eth.isTokenPocket ||
      eth.isOKExWallet ||
      eth.isBinance)
  );
};

/**
 * On mobile browsers (Safari/Chrome on iOS/Android), MetaMask is NOT injected
 * — `window.ethereum` is undefined. The fix is to deep-link the user into
 * MetaMask Mobile's in-app DApp browser. MetaMask supports a universal link:
 *   https://metamask.app.link/dapp/<host-without-https>
 * Returns true if a deep-link was opened, false otherwise.
 */
export function openInMetaMaskMobile() {
  if (typeof window === "undefined") return false;
  const host = window.location.host + window.location.pathname + window.location.search;
  const url = `https://metamask.app.link/dapp/${host}`;
  // Open in same tab so MetaMask takes over
  window.location.href = url;
  return true;
}

export async function ensureBscChain(provider, injected) {
  const eth = injected || window.ethereum;
  const net = await provider.getNetwork();
  if (Number(net.chainId) === 56) return;
  try {
    await requestWithWakeRetry(eth, {
      method: "wallet_switchEthereumChain",
      params: [{ chainId: BSC_CHAIN_ID_HEX }],
    });
  } catch (err) {
    if (err?.code === 4902) {
      await requestWithWakeRetry(eth, {
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: BSC_CHAIN_ID_HEX,
            chainName: "BNB Smart Chain",
            nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
            rpcUrls: ["https://bsc-dataseed.binance.org"],
            blockExplorerUrls: ["https://bscscan.com"],
          },
        ],
      });
    } else {
      throw err;
    }
  }
}

/**
 * Race a promise against a timeout. If timeout fires, reject with a friendly
 * error so the UI can recover gracefully instead of hanging forever.
 */
function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => {
      const err = new Error(
        `${label} timed out after ${Math.round(ms / 1000)}s. Open your wallet app, complete the prompt, then tap Connect again.`
      );
      err.code = "WALLET_TIMEOUT";
      reject(err);
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      }
    );
  });
}

/**
 * Full connect → sign → verify flow.
 * Returns { user, pro, session_token, config } on success.
 *
 * Mobile-MetaMask-safe: uses raw EIP-1193 `personal_sign` instead of ethers'
 * BrowserProvider.getSigner().signMessage() (which frequently hangs on mobile
 * MetaMask after you return from the app to sign).
 */
export async function connectAndVerify() {
  const injected = pickInjectedProvider();
  if (!injected) {
    const err = new Error(
      isMobile()
        ? "No wallet detected. Open this site inside the MetaMask or Trust Wallet app."
        : "No EVM wallet detected. Install MetaMask (https://metamask.io) or Trust Wallet."
    );
    err.code = isMobile() ? "NO_WALLET_MOBILE" : "NO_WALLET_DESKTOP";
    throw err;
  }

  // 1) request accounts — 60s timeout, with auto wake-retry on MetaMask MV3
  //    service-worker-dormant errors (e.g. "Broadcast channel unavailable").
  let accounts;
  try {
    accounts = await withTimeout(
      requestWithWakeRetry(injected, { method: "eth_requestAccounts" }),
      60_000,
      "Wallet connect"
    );
  } catch (e) {
    if (e?.code === 4001) {
      const err = new Error("Connection request rejected in wallet.");
      err.code = "USER_REJECTED";
      throw err;
    }
    if (isTransientWakeError(e)) {
      const err = new Error(
        "MetaMask didn't respond (service worker asleep or another wallet extension is interfering). Open the MetaMask extension once to unlock it, then click Connect again. If you have multiple wallet extensions installed, disable the extras and retry."
      );
      err.code = "WALLET_WAKE_FAIL";
      throw err;
    }
    throw e;
  }
  if (!accounts || !accounts.length) {
    throw new Error("Wallet returned no accounts. Unlock your wallet and try again.");
  }
  const address = String(accounts[0]).toLowerCase();

  // 2) ensure BSC chain — 45s timeout; if user cancels, surface that
  try {
    const provider = new BrowserProvider(injected);
    await withTimeout(ensureBscChain(provider, injected), 45_000, "Network switch");
  } catch (e) {
    if (e?.code === 4001) {
      const err = new Error("Network switch to BSC rejected in wallet.");
      err.code = "USER_REJECTED";
      throw err;
    }
    if (e?.code === "WALLET_TIMEOUT") throw e;
    // non-fatal if already on BSC and the call just failed to roundtrip
    console.warn("ensureBscChain failed (continuing):", e?.message || e);
  }

  // 3) ask backend for the challenge message
  const { message } = await walletChallenge(address);

  // 4) sign — use raw EIP-1193 personal_sign for mobile-MetaMask reliability.
  //    BrowserProvider.signMessage() often hangs after MetaMask app switch on
  //    iOS/Android. The raw RPC pattern is the EIP-191 canonical and resolves
  //    cleanly on every mobile wallet we've tested.
  let signature;
  try {
    signature = await withTimeout(
      requestWithWakeRetry(injected, {
        method: "personal_sign",
        params: [message, address],
      }),
      120_000,
      "Wallet signature"
    );
  } catch (e) {
    if (e?.code === 4001 || e?.code === "ACTION_REJECTED") {
      const err = new Error("Sign-in signature rejected in wallet.");
      err.code = "USER_REJECTED";
      throw err;
    }
    throw e;
  }

  // 5) backend verify
  return walletVerify(address, signature);
}

// ---------------------------------------------------------------------------
// WalletConnect (mobile-first path)
// ---------------------------------------------------------------------------
// Desktop users keep using the injected `window.ethereum` flow above. Mobile
// users (regular Safari/Chrome, NOT inside a wallet DApp browser) get this
// WalletConnect path: a modal opens with deep-links to MetaMask, Trust,
// Rainbow, etc. The user is sent into the wallet app to approve, then bounced
// back to mfsmaioracle.com with an active session. No page navigation, no
// "DApp browser" detour.

let _wcProvider = null;

async function _initWcProvider() {
  if (_wcProvider) return _wcProvider;
  if (!WC_PROJECT_ID) {
    throw new Error(
      "WalletConnect is not configured. Set REACT_APP_WALLETCONNECT_PROJECT_ID."
    );
  }
  // Dynamic import — keeps initial bundle small (only loaded on mobile).
  const { EthereumProvider } = await import(
    "@walletconnect/ethereum-provider"
  );
  _wcProvider = await EthereumProvider.init({
    projectId: WC_PROJECT_ID,
    chains: [BSC_CHAIN_ID],
    showQrModal: true,
    methods: ["eth_requestAccounts", "personal_sign", "eth_chainId", "eth_accounts"],
    events: ["chainChanged", "accountsChanged", "disconnect"],
    metadata: {
      name: "MFSMAI Oracle",
      description:
        "Live AI predictions for Polymarket. Pro unlocked by holding 5,000+ MFSMAI on BSC.",
      url:
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "https://mfsmaioracle.com",
      icons: ["https://mfsmaioracle.com/favicon.ico"],
    },
  });
  return _wcProvider;
}

/**
 * Mobile-friendly connect flow via WalletConnect v2.
 * Mirrors connectAndVerify(): same backend call, same return shape.
 */
export async function connectAndVerifyMobile() {
  const wc = await _initWcProvider();

  // Reuse an existing session if present (user already approved in a prior visit)
  let address;
  if (wc.accounts && wc.accounts.length > 0) {
    address = wc.accounts[0].toLowerCase();
  } else {
    try {
      await withTimeout(
        wc.connect({ chains: [BSC_CHAIN_ID] }),
        180_000,
        "Wallet connect"
      );
    } catch (e) {
      // EthereumProvider rejects with strings/errors when user cancels the modal
      const msg = (e && (e.message || String(e))) || "";
      if (
        msg.toLowerCase().includes("rejected") ||
        msg.toLowerCase().includes("closed") ||
        e?.code === 4001
      ) {
        const err = new Error("Connection cancelled in wallet.");
        err.code = "USER_REJECTED";
        throw err;
      }
      throw e;
    }
    const accts = wc.accounts || [];
    if (!accts.length) {
      throw new Error("Wallet returned no accounts. Try connecting again.");
    }
    address = accts[0].toLowerCase();
  }

  // Get challenge from backend
  const { message } = await walletChallenge(address);

  // Sign via WalletConnect's personal_sign RPC
  let signature;
  try {
    signature = await withTimeout(
      wc.request({
        method: "personal_sign",
        params: [message, address],
      }),
      180_000,
      "Wallet signature"
    );
  } catch (e) {
    const msg = (e && (e.message || String(e))) || "";
    if (
      e?.code === 4001 ||
      e?.code === "ACTION_REJECTED" ||
      msg.toLowerCase().includes("rejected")
    ) {
      const err = new Error("Sign-in signature rejected in wallet.");
      err.code = "USER_REJECTED";
      throw err;
    }
    throw e;
  }

  return walletVerify(address, signature);
}

/**
 * Disconnect the WalletConnect session (used on sign-out so the next sign-in
 * starts fresh). Safe to call when no session exists.
 */
export async function disconnectWalletConnect() {
  if (!_wcProvider) return;
  try {
    if (typeof _wcProvider.disconnect === "function") {
      await _wcProvider.disconnect();
    }
  } catch (e) {
    // ignore — disconnect is best-effort
  } finally {
    _wcProvider = null;
  }
}

